import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import {
  Plus, Bug, Lightbulb, AlertTriangle, Trash2, GripVertical,
  ArrowUp, ArrowDown, X, ChevronDown, Calendar, TrendingUp
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_MAP = {
  open: { label: "Open", color: "bg-slate-100 text-slate-700" },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-700" },
  deployed: { label: "Deployed", color: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700" },
  postponed: { label: "Postponed", color: "bg-amber-100 text-amber-700" },
};

const TYPE_MAP = {
  bug: { label: "Bug", icon: Bug, color: "text-red-600 bg-red-50 border-red-200" },
  feature: { label: "Feature", icon: Lightbulb, color: "text-violet-600 bg-violet-50 border-violet-200" },
};

function HeatBar({ count, max }) {
  const ratio = max > 0 ? Math.min(count / max, 1) : 0;
  const r = Math.round(180 + ratio * 75);
  const g = Math.round(200 - ratio * 180);
  const b = Math.round(200 - ratio * 180);
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.max(ratio * 100, 8)}%`, backgroundColor: `rgb(${r},${g},${b})` }}
        />
      </div>
      <span className="text-sm font-bold tabular-nums" style={{ color: `rgb(${r},${g},${b})` }}>{count}</span>
    </div>
  );
}

function ClientBadges({ clientIds, clientsMap }) {
  if (!clientIds?.length) return <span className="text-xs text-muted-foreground">-</span>;
  const shown = clientIds.slice(0, 3);
  const rest = clientIds.length - 3;
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map(id => (
        <span key={id} className="text-xs bg-muted px-1.5 py-0.5 rounded truncate max-w-[100px]">
          {clientsMap[id] || id.slice(0, 8)}
        </span>
      ))}
      {rest > 0 && <span className="text-xs text-muted-foreground">+{rest}</span>}
    </div>
  );
}

function ImpactHeat({ value, max, label }) {
  if (!value || value <= 0) return <span className="text-xs text-muted-foreground">-</span>;
  const ratio = max > 0 ? Math.min(value / max, 1) : 0;
  // Green (low) → Yellow (mid) → Red (high)
  let r, g, b;
  if (ratio < 0.5) {
    const t = ratio * 2;
    r = Math.round(80 + t * 175);
    g = Math.round(180 - t * 50);
    b = Math.round(80 - t * 40);
  } else {
    const t = (ratio - 0.5) * 2;
    r = Math.round(255 - t * 30);
    g = Math.round(130 - t * 100);
    b = Math.round(40 - t * 20);
  }
  const color = `rgb(${r},${g},${b})`;
  const bgAlpha = 0.1 + ratio * 0.15;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold tabular-nums"
      style={{ color, backgroundColor: `rgba(${r},${g},${b},${bgAlpha})` }}
    >
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}


// ===== CREATE / EDIT DIALOG =====
function TrackerDialog({ open, onClose, item, clients, userRole, onSave, maxImpactArr }) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    type: "bug", title: "", description: "", client_ids: [], is_urgent: false,
    status: "open", deployment_estimated: ""
  });
  const [saving, setSaving] = useState(false);
  const [clientSearch, setClientSearch] = useState("");

  useEffect(() => {
    if (item) {
      setForm({
        type: item.type, title: item.title, description: item.description || "",
        client_ids: item.client_ids || [], is_urgent: item.is_urgent || false,
        status: item.status || "open", deployment_estimated: item.deployment_estimated || ""
      });
    } else {
      setForm({ type: "bug", title: "", description: "", client_ids: [], is_urgent: false, status: "open", deployment_estimated: "" });
    }
    setClientSearch("");
  }, [item, open]);

  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients.slice(0, 20);
    const q = clientSearch.toLowerCase();
    return clients.filter(c => c.name.toLowerCase().includes(q)).slice(0, 20);
  }, [clients, clientSearch]);

  const toggleClient = (clientId) => {
    setForm(p => ({
      ...p,
      client_ids: p.client_ids.includes(clientId)
        ? p.client_ids.filter(id => id !== clientId)
        : [...p.client_ids, clientId]
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          title: form.title, description: form.description,
          client_ids: form.client_ids, is_urgent: form.is_urgent,
        };
        if (userRole === "admin") {
          payload.status = form.status;
          payload.deployment_estimated = form.deployment_estimated || null;
        }
        await axios.put(`${API}/tracker/${item.item_id}`, payload);
        toast.success("Item aggiornato");
      } else {
        await axios.post(`${API}/tracker`, {
          type: form.type, title: form.title, description: form.description,
          client_ids: form.client_ids, is_urgent: form.is_urgent,
        });
        toast.success("Item creato");
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tracker-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica Item" : "Nuovo Item"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          {/* Type selector (only for new) */}
          {!isEdit && (
            <div className="space-y-1.5">
              <Label>Tipologia</Label>
              <div className="flex gap-2">
                {["bug", "feature"].map(t => {
                  const T = TYPE_MAP[t];
                  const Icon = T.icon;
                  return (
                    <button key={t} onClick={() => setForm(p => ({ ...p, type: t }))}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-medium transition-all ${form.type === t ? T.color + " border-current" : "border-border text-muted-foreground hover:bg-muted"}`}
                      data-testid={`tracker-type-${t}`}>
                      <Icon className="h-4 w-4" />{T.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Title */}
          <div className="space-y-1.5">
            <Label>Titolo</Label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Descrivi brevemente..." data-testid="tracker-title" />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label>Descrizione</Label>
            <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Dettagli, step per riprodurre..." data-testid="tracker-description" />
          </div>

          {/* Urgent toggle */}
          <div className="flex items-center gap-3">
            <button onClick={() => setForm(p => ({ ...p, is_urgent: !p.is_urgent }))}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium transition-all ${form.is_urgent ? "bg-red-50 text-red-700 border-red-300" : "border-border text-muted-foreground hover:bg-muted"}`}
              data-testid="tracker-urgent-toggle">
              <AlertTriangle className="h-4 w-4" />
              {form.is_urgent ? "Urgente" : "Segna come Urgente"}
            </button>
          </div>

          {/* Clients multi-select */}
          <div className="space-y-1.5">
            <Label>Clienti Associati</Label>
            {form.client_ids.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.client_ids.map(id => {
                  const cl = clients.find(c => c.client_id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                      {cl?.name || id}
                      <X className="h-3 w-3 cursor-pointer hover:text-red-500" onClick={() => toggleClient(id)} />
                    </span>
                  );
                })}
              </div>
            )}
            <Input placeholder="Cerca cliente..." value={clientSearch} onChange={e => setClientSearch(e.target.value)} data-testid="tracker-client-search" />
            <div className="max-h-[150px] overflow-y-auto border rounded-md mt-1">
              {filteredClients.map(c => (
                <button key={c.client_id} onClick={() => toggleClient(c.client_id)}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors ${form.client_ids.includes(c.client_id) ? "bg-primary/5 font-medium" : ""}`}>
                  {form.client_ids.includes(c.client_id) && <span className="text-primary mr-1">&#10003;</span>}
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {/* Admin-only: Status & Deployment Date */}
          {isEdit && userRole === "admin" && (
            <div className="border-t pt-4 space-y-4">
              <p className="text-xs font-medium text-muted-foreground uppercase">Gestione Admin</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} data-testid="tracker-status-select">
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="deployed">Deployed</option>
                    <option value="rejected">Rejected</option>
                    <option value="postponed">Postponed</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Deployment Stimato</Label>
                  <Input type="date" value={form.deployment_estimated || ""} onChange={e => setForm(p => ({ ...p, deployment_estimated: e.target.value }))} data-testid="tracker-deployment-date" />
                </div>
              </div>
            </div>
          )}

          {/* Occurrence log (edit only) */}
          {/* Occurrences counter (edit only) */}
          {isEdit && (
            <div className="border-t pt-4 space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase">Segnalazioni</p>
              <div className="flex items-center gap-3">
                <button
                  onClick={async (e) => {
                    e.preventDefault(); e.stopPropagation();
                    try {
                      await axios.post(`${API}/tracker/${item.item_id}/occurrence-remove`);
                      toast.success("Occorrenza rimossa");
                      onSave();
                    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
                  }}
                  disabled={item.occurrences <= 1}
                  className="flex items-center justify-center w-7 h-7 rounded-md border border-red-200 bg-red-50 text-red-600 text-sm font-bold hover:bg-red-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="tracker-remove-occurrence"
                  type="button"
                >-</button>
                <div className="flex items-center gap-1.5">
                  <span className="text-lg font-bold tabular-nums" data-testid="tracker-occurrence-count">{item.occurrences}</span>
                  <span className="text-xs text-muted-foreground">occorrenze</span>
                </div>
                <button
                  onClick={async (e) => {
                    e.preventDefault(); e.stopPropagation();
                    try {
                      await axios.post(`${API}/tracker/${item.item_id}/occurrence`);
                      toast.success("Occorrenza aggiunta");
                      onSave();
                    } catch { toast.error("Errore"); }
                  }}
                  className="flex items-center justify-center w-7 h-7 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-600 text-sm font-bold hover:bg-emerald-100 transition-colors"
                  data-testid="tracker-add-occurrence-dialog"
                  type="button"
                >+</button>
              </div>
              {item.occurrence_log?.length > 0 && (
                <div className="max-h-[100px] overflow-y-auto space-y-1 pt-1">
                  {[...item.occurrence_log].reverse().map((log, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{log.user_name}</span>
                      <span>{new Date(log.timestamp).toLocaleDateString('it-IT')} {new Date(log.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Impact (edit only) */}
          {isEdit && (item?.impact_arr > 0) && (
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                <span className="font-medium">Impatto Economico:</span>
                <ImpactHeat value={item.impact_arr} max={maxImpactArr} label={`\u20AC${item.impact_arr.toLocaleString('it-IT')} ARR`} />
                <span className="text-muted-foreground">({item.impact_contracts} contratti attivi)</span>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="tracker-save-btn">
              {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" /> : <Plus className="h-4 w-4" />}
              {isEdit ? "Salva Modifiche" : "Crea Item"}
            </Button>
            <Button variant="outline" onClick={onClose}>Annulla</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== MAIN TRACKER TAB =====
export default function TrackerTab({ userRole, clients, focusItem }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterType, setFilterType] = useState("");
  const ALL_STATUSES = ["open", "in_progress", "deployed", "rejected", "postponed"];
  const [filterStatuses, setFilterStatuses] = useState(["open", "in_progress", "rejected", "postponed"]);
  const [filterUrgent, setFilterUrgent] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [dragIndex, setDragIndex] = useState(null);
  const [statusDropOpen, setStatusDropOpen] = useState(false);
  const statusDropRef = useRef(null);

  useEffect(() => {
    if (!statusDropOpen) return;
    const handler = (e) => { if (statusDropRef.current && !statusDropRef.current.contains(e.target)) setStatusDropOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [statusDropOpen]);

  const clientsMap = useMemo(() => {
    const m = {};
    (clients || []).forEach(c => { m[c.client_id] = c.name; });
    return m;
  }, [clients]);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tracker`);
      const newItems = res.data.items || [];
      setItems(newItems);
      // Keep editingItem in sync if dialog is open
      setEditingItem(prev => {
        if (!prev) return prev;
        const updated = newItems.find(i => i.item_id === prev.item_id);
        return updated || prev;
      });
    } catch { toast.error("Errore nel caricamento tracker"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    if (!focusItem?.id) return;
    setFilterType(""); setFilterUrgent(""); setFilterStatuses(ALL_STATUSES);
    setHighlightId(focusItem.id);
    const t = setTimeout(() => setHighlightId(null), 5000);
    return () => clearTimeout(t);
  }, [focusItem]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (highlightId && !loading) document.querySelector(`[data-testid="tracker-row-${highlightId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightId, loading]);

  const maxOccurrences = useMemo(() => Math.max(...items.map(i => i.occurrences || 1), 1), [items]);
  const maxImpactArr = useMemo(() => Math.max(...items.map(i => i.impact_arr || 0), 1), [items]);

  const filteredItems = useMemo(() => {
    let list = [...items];
    if (filterType) list = list.filter(i => i.type === filterType);
    if (filterStatuses.length < ALL_STATUSES.length) {
      list = list.filter(i => filterStatuses.includes(i.status));
    }
    if (filterUrgent === "yes") list = list.filter(i => i.is_urgent);
    if (filterUrgent === "no") list = list.filter(i => !i.is_urgent);

    if (sortCol) {
      list.sort((a, b) => {
        let va, vb;
        switch (sortCol) {
          case "occurrences": va = a.occurrences; vb = b.occurrences; break;
          case "urgent": va = a.is_urgent ? 1 : 0; vb = b.is_urgent ? 1 : 0; break;
          case "date": va = a.created_at || ""; vb = b.created_at || ""; break;
          case "deployment": va = a.deployment_estimated || "9999"; vb = b.deployment_estimated || "9999"; break;
          case "impact": va = a.impact_arr || 0; vb = b.impact_arr || 0; break;
          case "status": va = a.status; vb = b.status; break;
          default: va = a.sort_order; vb = b.sort_order;
        }
        if (va < vb) return sortDir === "asc" ? -1 : 1;
        if (va > vb) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [items, filterType, filterStatuses, filterUrgent, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronDown className="h-3 w-3 opacity-30" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  const handleAddOccurrence = async (itemId, e) => {
    e.stopPropagation();
    try {
      const res = await axios.post(`${API}/tracker/${itemId}/occurrence`);
      setItems(prev => prev.map(i => i.item_id === itemId ? res.data : i));
    } catch { toast.error("Errore"); }
  };

  const handleDelete = async (itemId, e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare questo item?")) return;
    try {
      await axios.delete(`${API}/tracker/${itemId}`);
      toast.success("Item eliminato");
      setItems(prev => prev.filter(i => i.item_id !== itemId));
    } catch { toast.error("Errore"); }
  };

  // Drag & Drop
  const handleDragStart = (index) => { setDragIndex(index); };
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const newItems = [...filteredItems];
    const [removed] = newItems.splice(dragIndex, 1);
    newItems.splice(index, 0, removed);
    const reordered = newItems.map((item, i) => ({ ...item, sort_order: i }));
    setItems(prev => {
      const idSet = new Set(reordered.map(r => r.item_id));
      const untouched = prev.filter(p => !idSet.has(p.item_id));
      return [...reordered, ...untouched];
    });
    setDragIndex(index);
  };
  const handleDragEnd = async () => {
    setDragIndex(null);
    const order = filteredItems.map((item, i) => ({ item_id: item.item_id, sort_order: i }));
    try {
      await axios.put(`${API}/tracker/reorder`, { order });
    } catch { /* silent */ }
  };

  const fmtC = (v) => `\u20AC${(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 0 })}`;

  // Stats
  const stats = useMemo(() => {
    const bugs = items.filter(i => i.type === "bug");
    const features = items.filter(i => i.type === "feature");
    const urgent = items.filter(i => i.is_urgent);
    const open = items.filter(i => i.status === "open" || i.status === "in_progress");
    return { bugs: bugs.length, features: features.length, urgent: urgent.length, open: open.length };
  }, [items]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="tracker-tab">
      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-red-200/60">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase">Bug</p>
            <p className="text-2xl font-bold mt-1 text-red-600" data-testid="tracker-stat-bugs">{stats.bugs}</p>
          </CardContent>
        </Card>
        <Card className="border-violet-200/60">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase">Feature</p>
            <p className="text-2xl font-bold mt-1 text-violet-600" data-testid="tracker-stat-features">{stats.features}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200/60">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase">Urgenti</p>
            <p className="text-2xl font-bold mt-1 text-amber-600" data-testid="tracker-stat-urgent">{stats.urgent}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200/60">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase">Aperti / In Progress</p>
            <p className="text-2xl font-bold mt-1 text-blue-600" data-testid="tracker-stat-open">{stats.open}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters + Create */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={filterType} onChange={e => setFilterType(e.target.value)} data-testid="tracker-filter-type">
            <option value="">Tutti i tipi</option>
            <option value="bug">Bug</option>
            <option value="feature">Feature</option>
          </select>
          <div className="relative" data-testid="tracker-filter-status" ref={statusDropRef}>
            <button
              onClick={() => setStatusDropOpen(p => !p)}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs inline-flex items-center gap-1 min-w-[140px]"
            >
              {filterStatuses.length === ALL_STATUSES.length ? "Tutti gli status" : filterStatuses.length === 0 ? "Nessuno" : `${filterStatuses.length} status`}
              <ChevronDown className="h-3 w-3 ml-auto" />
            </button>
            {statusDropOpen && (
              <div className="absolute top-9 left-0 z-50 bg-background border rounded-md shadow-lg py-1 min-w-[160px]">
                <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors text-muted-foreground"
                  onClick={() => { setFilterStatuses(filterStatuses.length === ALL_STATUSES.length ? [] : [...ALL_STATUSES]); }}>
                  {filterStatuses.length === ALL_STATUSES.length ? "Deseleziona tutti" : "Seleziona tutti"}
                </button>
                <div className="border-t my-1" />
                {ALL_STATUSES.map(s => {
                  const S = STATUS_MAP[s];
                  const checked = filterStatuses.includes(s);
                  return (
                    <button key={s} className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors flex items-center gap-2"
                      onClick={() => setFilterStatuses(prev => checked ? prev.filter(x => x !== s) : [...prev, s])}>
                      <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${checked ? "bg-primary border-primary text-white" : "border-input"}`}>
                        {checked && <span className="text-[10px]">&#10003;</span>}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded-full text-xs ${S.color}`}>{S.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={filterUrgent} onChange={e => setFilterUrgent(e.target.value)} data-testid="tracker-filter-urgent">
            <option value="">Urgenza: tutti</option>
            <option value="yes">Solo urgenti</option>
            <option value="no">Non urgenti</option>
          </select>
        </div>
        <Button className="gap-2" onClick={() => { setEditingItem(null); setDialogOpen(true); }} data-testid="tracker-create-btn">
          <Plus className="h-4 w-4" />
          Nuovo Item
        </Button>
      </div>

      {/* Table */}
      <Card className="border-border/60">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="w-8 p-3"></th>
                <th className="text-left p-3 font-medium text-muted-foreground w-16">Tipo</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Titolo</th>
                <th className="text-left p-3 font-medium text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort("occurrences")} data-testid="tracker-sort-occurrences">
                  <span className="inline-flex items-center gap-1">Occ. <SortIcon col="occurrences" /></span>
                </th>
                <th className="text-left p-3 font-medium text-muted-foreground">Clienti</th>
                <th className="text-left p-3 font-medium text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort("impact")} data-testid="tracker-sort-impact">
                  <span className="inline-flex items-center gap-1">Impatto ARR <SortIcon col="impact" /></span>
                </th>
                <th className="text-left p-3 font-medium text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort("urgent")} data-testid="tracker-sort-urgent">
                  <span className="inline-flex items-center gap-1">Urg. <SortIcon col="urgent" /></span>
                </th>
                <th className="text-left p-3 font-medium text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort("status")} data-testid="tracker-sort-status">
                  <span className="inline-flex items-center gap-1">Status <SortIcon col="status" /></span>
                </th>
                <th className="text-left p-3 font-medium text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort("deployment")} data-testid="tracker-sort-deployment">
                  <span className="inline-flex items-center gap-1">Deploy <SortIcon col="deployment" /></span>
                </th>
                <th className="text-right p-3 font-medium text-muted-foreground w-20">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr><td colSpan={10} className="p-12 text-center text-muted-foreground">Nessun item. Crea il primo bug o feature request.</td></tr>
              ) : filteredItems.map((item, index) => {
                const T = TYPE_MAP[item.type] || TYPE_MAP.bug;
                const S = STATUS_MAP[item.status] || STATUS_MAP.open;
                const Icon = T.icon;
                return (
                  <tr key={item.item_id}
                    className={`border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-colors duration-700 ${dragIndex === index ? "opacity-50" : ""} ${highlightId === item.item_id ? "bg-amber-100 ring-2 ring-amber-400 ring-inset" : ""}`}
                    onClick={() => { setEditingItem(item); setDialogOpen(true); }}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    data-testid={`tracker-row-${item.item_id}`}
                  >
                    <td className="p-2 text-center cursor-grab active:cursor-grabbing" onClick={e => e.stopPropagation()}>
                      <GripVertical className="h-4 w-4 text-muted-foreground/50" />
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${T.color}`}>
                        <Icon className="h-3 w-3" />
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.title}</span>
                        {item.is_urgent && <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
                      </div>
                      {item.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item.description}</p>}
                    </td>
                    <td className="p-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <HeatBar count={item.occurrences} max={maxOccurrences} />
                        <button onClick={(e) => handleAddOccurrence(item.item_id, e)}
                          className="text-xs bg-muted hover:bg-primary/10 hover:text-primary px-1.5 py-0.5 rounded font-medium transition-colors"
                          data-testid={`tracker-add-occ-${item.item_id}`}>
                          +1
                        </button>
                      </div>
                    </td>
                    <td className="p-3"><ClientBadges clientIds={item.client_ids} clientsMap={clientsMap} /></td>
                    <td className="p-3">
                      <ImpactHeat value={item.impact_arr} max={maxImpactArr} label={fmtC(item.impact_arr)} />
                    </td>
                    <td className="p-3">
                      {item.is_urgent && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          <AlertTriangle className="h-3 w-3" />Urgente
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${S.color}`}>{S.label}</span>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {item.deployment_estimated ? (
                        <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(item.deployment_estimated).toLocaleDateString('it-IT')}</span>
                      ) : "-"}
                    </td>
                    <td className="p-3 text-right" onClick={e => e.stopPropagation()}>
                      {userRole === "admin" && (
                        <button onClick={(e) => handleDelete(item.item_id, e)}
                          className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
                          data-testid={`tracker-delete-${item.item_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <TrackerDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingItem(null); }}
        item={editingItem}
        clients={clients || []}
        userRole={userRole}
        onSave={fetchItems}
        maxImpactArr={maxImpactArr}
      />
    </div>
  );
}
